import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export interface BreadcrumbItem {
  name: string;
  href: string;
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      item: `${SITE_URL}${item.href}`,
    })),
  };
}

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  pathname?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  ogType?: string;
}

export default function SEO({
  title,
  description,
  keywords,
  pathname = '',
  schema,
  noindex = false,
  ogType = 'website',
}: SEOProps) {
  useEffect(() => {
    document.title = title;

    setMetaTag('name', 'description', description);

    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    }

    if (noindex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      removeMetaTag('name', 'robots');
    }

    const canonicalUrl = pathname ? `${SITE_URL}${pathname}` : SITE_URL;
    setLinkTag('canonical', canonicalUrl);

    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:site_name', 'Fore Capital');

    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);

    const today = new Date().toISOString().split('T')[0];
    setMetaTag('name', 'last-modified', today);

    // Remove all previously injected JSON-LD scripts
    document.querySelectorAll('script[data-json-ld]').forEach(el => el.remove());

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s, idx) => {
        const jsonLdScript = document.createElement('script');
        jsonLdScript.setAttribute('data-json-ld', String(idx));
        jsonLdScript.type = 'application/ld+json';
        jsonLdScript.textContent = JSON.stringify(s);
        document.head.appendChild(jsonLdScript);
      });
    }
  }, [title, description, keywords, pathname, schema, noindex, ogType]);

  return null;
}

function setMetaTag(attrType: string, attrValue: string, content: string) {
  let meta = document.querySelector(`meta[${attrType}="${attrValue}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attrType, attrValue);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeMetaTag(attrType: string, attrValue: string) {
  const meta = document.querySelector(`meta[${attrType}="${attrValue}"]`);
  if (meta) {
    meta.remove();
  }
}

function setLinkTag(rel: string, href: string) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}